import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { listenToPublishedProducts, Product } from "../../redux/productSlice";
import { AppDispatch } from "../../redux/store";
/* ── CART ADAPTER: same names as Shop.tsx. If your cartSlice differs, only these 4 lines change ── */
import { addToCart, decrementCartItem } from "../../redux/cartSlice";
import Footer from "../../GLOBAL/Footer";

const selectCartItems = (s: any): any[] => s.cart?.items ?? [];
const lineQty = (i: any): number => Number(i?.quantity ?? i?.qty ?? 1);
const lineId = (i: any): string => String(i?.productId ?? i?.listingId ?? "");


const BRAND = "BEAUTABLES";
const MAX_RELATED = 8;
const CHECKOUT_PATH = "/checkout";
const DETAILS_PATH = (id: string) => `/details/${id}`;

/* ── palette (same as Home / Coming Soon) ── */
const LIME = "#C0DC6C";
const RASP = "#FB2564";
const YEL = "#F8DA43";
const PINK = "#FF6BB5";

/* ── helpers ── */
type Variant = { color: string; hex?: string | null; images?: string[]; stock?: { size: string; qty: number }[] };
type Pick = { color?: string; size?: string };

const money = (v: number) => `€${(Number(v) || 0).toFixed(2)}`;
const stockOf = (p: Product) => Math.max(0, Number((p as any).quantity) || 0);
const variantsOf = (p: Product): Variant[] => (((p as any).variants as Variant[]) || []).filter((v) => v && v.color);
const variantQty = (v: Variant) => (v.stock || []).reduce((s, x) => s + (Number(x.qty) || 0), 0);
const coverOf = (p: Product): string | undefined =>
  (p as any).images?.[0] || variantsOf(p).flatMap((v) => v.images || [])[0];
const attrText = (v: string | string[]) => (Array.isArray(v) ? v.join(", ") : String(v));
const TOP_SPECS = ["Brand", "Model", "Condition", "Warranty"];
const specsOf = (p: Product): [string, string][] =>
  Object.entries(((p as any).attributes || {}) as Record<string, string | string[]>)
    .filter(([k, v]) => k !== "Color" && v != null && attrText(v).trim() !== "")
    .sort(([a], [b]) => {
      const ia = TOP_SPECS.indexOf(a), ib = TOP_SPECS.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    })
    .map(([k, v]) => [k, attrText(v)]);

/* the box-art flower */
const Flower = ({ className, petal = RASP, centre = YEL }: { className?: string; petal?: string; centre?: string }) => (
  <svg className={className} viewBox="-100 -100 200 200" aria-hidden="true">
    {[0, 90, 180, 270].map((r) => (
      <ellipse key={r} cx="0" cy="-52" rx="34" ry="46" fill={petal} stroke="#000" strokeWidth="3" transform={`rotate(${r + 12})`} />
    ))}
    <circle r="27" fill={centre} stroke="#000" strokeWidth="3" />
  </svg>
);

/* ═══════════════════════════ THE PRODUCT (own component, keyed by productId so state resets per product) ═══════════════════════════ */
const ProductView = ({ p, all }: { p: Product; all: Product[] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const cartItems = useSelector(selectCartItems);

  const variants = variantsOf(p);
  const [idx, setIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [color, setColor] = useState<string | null>(
    () => (variants.find((v) => variantQty(v) > 0) ?? variants[0])?.color ?? null
  );
  const [size, setSize] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  const cur = variants.find((v) => v.color === color);
  const mainImgs: string[] = (p as any).images ?? [];
  const imgs: string[] = cur?.images?.length ? cur.images : mainImgs.length ? mainImgs : variants.flatMap((v) => v.images || []);
  const sizeRows = (cur?.stock || []).filter((s) => s.size !== "-");
  const needsSize = sizeRows.length > 0;
  const needPick = needsSize && !size;

  const inBag = cartItems.filter((i: any) => lineId(i) === p.productId).reduce((s: number, i: any) => s + lineQty(i), 0);
  const stock = stockOf(p);
  const price = Number((p as any).price) || 0;
  const pickQty = !cur ? Infinity : needsSize ? (size ? Number(sizeRows.find((s) => s.size === size)?.qty) || 0 : Infinity) : variantQty(cur);
  const room = Math.max(0, Math.min(stock - inBag, pickQty));
  const maxed = room <= 0;
  const q = Math.min(qty, Math.max(1, room));
  const pick: Pick = { color: cur?.color, size: size ?? undefined };
  const specs = specsOf(p);
  const where = [(p as any).city, (p as any).country].filter(Boolean).join(", ");
  const description = String((p as any).description || "").trim();

  const buildLine = (n: number) => ({
    productId: p.productId,
    name: p.name,
    price,
    quantity: n,
    photo: cur?.images?.[0] || coverOf(p) || "",
    ownerId: (p as any).ownerId ?? "",
    ...(pick.color ? { color: pick.color } : {}),
    ...(pick.size ? { size: pick.size } : {}),
  });

  const go = (d: number) => {
    if (imgs.length) setIdx((i) => (i + d + imgs.length) % imgs.length);
  };

  const addNow = () => {
    if (maxed || needPick) return;
    dispatch(addToCart(buildLine(q) as any) as any);
    setToast(`${p.name} is in your bag ♡`);
  };

  const toCheckout = (items: any[]) => {
    if (!items.length) { setToast("Your bag is empty"); return; }
    navigate(CHECKOUT_PATH, { state: { items } });
  };

  const buyNow = () => {
    if (inBag > 0 && maxed) { toCheckout(cartItems); return; }
    if (maxed || needPick) return;
    const line = buildLine(q);
    dispatch(addToCart(line as any) as any);
    // the store hasn't re-rendered yet, so hand checkout the cart including this line
    const at = cartItems.findIndex((i: any) => lineId(i) === line.productId);
    const next =
      at < 0
        ? [...cartItems, line]
        : cartItems.map((i: any, k: number) => (k === at ? { ...i, quantity: lineQty(i) + q } : i));
    toCheckout(next);
  };

  /* ── related: same sub-category first, then same category, then same gender, then newest ── */
  const related = useMemo(() => {
    const sub = (p as any).subcategoryId, cat = (p as any).categoryId;
    const genders: string[] = (p as any).gender || [];
    const score = (o: Product) => {
      let s = 0;
      if (sub && (o as any).subcategoryId === sub) s += 4;
      if (cat && (o as any).categoryId === cat) s += 3;
      if (genders.length && ((o as any).gender || []).some((g: string) => genders.includes(g))) s += 1;
      return s;
    };
    return all
      .filter((o) => o.productId !== p.productId && (o as any).published !== false && stockOf(o) > 0 && coverOf(o))
      .map((o) => ({ o, s: score(o) }))
      .sort((a, b) => b.s - a.s || (b.o.createdAt ?? 0) - (a.o.createdAt ?? 0))
      .slice(0, MAX_RELATED)
      .map((x) => x.o);
  }, [all, p]);

  return (
    <>
      <section className="bd-product">
        {/* ── gallery ── */}
        <div className="bd-gal">
          <div className="bd-frame">
            <span className="bd-sticker">New ✦</span>
            {imgs[idx] ? (
              <img key={imgs[idx]} src={imgs[idx]} alt={p.name} draggable={false} />
            ) : (
              <div className="bd-noimg">♡</div>
            )}
            {imgs.length > 1 && (
              <>
                <button type="button" className="bd-arrow l" aria-label="Previous photo" onClick={() => go(-1)}>‹</button>
                <button type="button" className="bd-arrow r" aria-label="Next photo" onClick={() => go(1)}>›</button>
              </>
            )}
          </div>
          {imgs.length > 1 && (
            <div className="bd-thumbs">
              {imgs.map((u, i) => (
                <button type="button" key={u + i} className={`bd-thumb${i === idx ? " on" : ""}`} aria-label={`Photo ${i + 1}`} onClick={() => setIdx(i)}>
                  <img src={u} alt="" draggable={false} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── info ── */}
        <div className="bd-info">
          <h1 className="bd-name">{p.name}</h1>
          <div className="bd-pricerow">
            <span className="bd-price">{money(price)}</span>
            <span className={`bd-stock${stock <= 5 ? " low" : ""}`}>{stock <= 5 ? `Only ${stock} left ♡` : "In stock ✦"}</span>
          </div>

          {description && <p className="bd-desc">{description}</p>}

          {variants.length > 0 && (
            <div className="bd-opt">
              <h3>Pick a colour{cur ? <span>: {cur.color}</span> : null}</h3>
              <div className="bd-colors">
                {variants.map((v) => (
                  <button
                    type="button" key={v.color} aria-label={v.color} aria-pressed={v.color === color}
                    className={`bd-dot${v.color === color ? " on" : ""}${variantQty(v) <= 0 ? " gone" : ""}`}
                    onClick={() => { setColor(v.color); setSize(null); setIdx(0); setQty(1); }}
                  >
                    <i style={{ background: v.hex || `conic-gradient(${RASP}, ${YEL}, ${LIME}, ${PINK}, ${RASP})` }} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {needsSize && (
            <div className="bd-opt">
              <h3>Pick a size{size ? <span>: {size}</span> : null}</h3>
              <div className="bd-sizes">
                {sizeRows.map((s) => (
                  <button
                    type="button" key={s.size} className={`bd-size${size === s.size ? " on" : ""}`}
                    disabled={(Number(s.qty) || 0) <= 0} aria-pressed={size === s.size}
                    onClick={() => { setSize(size === s.size ? null : s.size); setQty(1); }}
                  >
                    {s.size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(specs.length > 0 || where) && (
            <div className="bd-specs">
              {specs.map(([k, v]) => (
                <div key={k}><small>{k}</small><span>{v}</span></div>
              ))}
              {where && <div><small>Ships from</small><span>{where}</span></div>}
            </div>
          )}

          {maxed && inBag > 0 && <p className="bd-note">All {stock} are already in your bag ♡</p>}

          <div className="bd-total">
            <span>{inBag > 0 ? "In your bag" : "Total"}</span>
            <b>{money(price * (inBag > 0 ? inBag : q))}</b>
          </div>

          <div className="bd-buyrow">
            {inBag > 0 ? (
              <div className="bd-qty">
                <button type="button" aria-label="Remove one" onClick={() => dispatch(decrementCartItem({ productId: p.productId } as any) as any)}>−</button>
                <b>{inBag}</b>
                <button type="button" aria-label="Add one" disabled={maxed || needPick} onClick={addNow}>+</button>
              </div>
            ) : (
              <>
                <div className="bd-qty">
                  <button type="button" aria-label="Less" disabled={maxed || q <= 1} onClick={() => setQty(Math.max(1, q - 1))}>−</button>
                  <b>{q}</b>
                  <button type="button" aria-label="More" disabled={maxed || q >= room} onClick={() => setQty(Math.min(room, q + 1))}>+</button>
                </div>
                <button type="button" className="bt-btn sun grow" disabled={maxed || needPick} onClick={addNow}>
                  {needPick ? "Choose a size" : "Add to bag ♡"}
                </button>
              </>
            )}
          </div>

          <button type="button" className="bt-btn big block" disabled={stock <= 0 || (inBag === 0 && (maxed || needPick))} onClick={buyNow}>
            {inBag > 0 ? "Go to checkout ✦" : "Buy now ✦"}
          </button>
        </div>
      </section>

      {/* ── related ── */}
      {related.length > 0 && (
        <section className="bd-related" aria-label="Related products">
          <h2 className="bd-rtitle">You might also love ♡</h2>
          <p className="bd-rsub">More cute picks, just for you</p>
          <div className="bd-rgrid">
            {related.map((o) => (
              <button type="button" key={o.productId} className="bd-rcard" onClick={() => navigate(DETAILS_PATH(o.productId))} aria-label={`View ${o.name}`}>
                <img src={coverOf(o)} alt={o.name} loading="lazy" draggable={false} />
                <span className="bd-rchip">{stockOf(o) <= 5 ? "Almost gone" : "New ✦"}</span>
                <span className="bd-rinfo">
                  <b>{o.name}</b>
                  <em>{money(Number((o as any).price) || 0)}</em>
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {toast && <div className="bd-toast" role="status">{toast}</div>}
    </>
  );
};

/* ═══════════════════════════ PAGE ═══════════════════════════ */
const Details = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { productId } = useParams<{ productId: string }>();
  const allProducts = useSelector((st: any) => st.products?.products) as Product[] | undefined;
  const cartItems = useSelector(selectCartItems);
  const cartCount = cartItems.reduce((s: number, i: any) => s + lineQty(i), 0);

  useEffect(() => {
    const unsub = dispatch(listenToPublishedProducts() as any) as any;
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [dispatch]);

  /* page chrome: no sideways scroll; jump to the top when you open another product */
  useEffect(() => {
    const prev = document.body.style.overflowX;
    document.body.style.overflowX = "hidden";
    document.body.style.margin = "0";
    return () => {
      document.body.style.overflowX = prev;
    };
  }, []);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productId]);

  const product = (allProducts ?? []).find((p) => p.productId === productId);
  const loading = allProducts === undefined || (allProducts.length === 0 && !product);

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Chewy&family=Fredoka:wght@400;500;600;700&display=swap');

    .bd-root{
      --ink:#000; --pink:#FF6BB5; --pink-soft:#FFD3EA; --rasp:#E30B5C; --rasp-dark:#8F0636;
      --yel:#FFD60A; --yel-dark:#C99700; --lime:#C8E17A; --lime-dark:#7E9A38; --red:#E10000;
      position:relative; isolation:isolate; width:100%; max-width:100vw; min-height:100dvh; overflow-x:hidden; color:var(--ink);
      font-family:'Fredoka',system-ui,sans-serif; box-sizing:border-box; display:flex; flex-direction:column;
      background-color:#FFF1F8;
      background-image:radial-gradient(circle at 20% 0%, #fff 0, rgba(255,255,255,0) 45%), radial-gradient(var(--pink-soft) 2.2px, transparent 2.8px);
      background-size:auto, 30px 30px;
    }
    .bd-root *, .bd-root *::before, .bd-root *::after{ box-sizing:border-box; font-family:inherit; }
    .bd-root button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
    .bd-root button:disabled{ cursor:not-allowed; opacity:.5; }
    .bd-root button:focus-visible{ outline:3px solid var(--ink); outline-offset:3px; }

    .bd-stripes{ position:absolute; inset:0; z-index:-1; pointer-events:none;
      background:
        radial-gradient(rgba(255,255,255,.55) 2.2px, transparent 2.8px) 0 0 / 30px 30px,
        repeating-linear-gradient(104deg, #FFD3EA 0 130px, #FFF08A 130px 260px, #D7EC9B 260px 390px, #FFB3D1 390px 520px, #FFC9A0 520px 650px); }
    .bd-flower{ position:absolute; z-index:0; pointer-events:none; }
    .bd-flower.a{ width:240px; height:240px; left:-110px; top:22%; transform:rotate(-12deg); }
    .bd-flower.b{ width:200px; height:200px; right:-90px; top:8%; transform:rotate(18deg); }
    .bd-flower.c{ width:280px; height:280px; right:-130px; top:52%; transform:rotate(-8deg); }
    .bd-flower.d{ width:170px; height:170px; left:-70px; top:70%; transform:rotate(24deg); }

    .bd-mark{ font-family:'Chewy','Fredoka',cursive !important; font-weight:400; letter-spacing:.05em; line-height:1; color:var(--pink);
      paint-order:stroke fill; -webkit-text-stroke:7px var(--ink); text-shadow:0 4px 0 var(--rasp); margin:0; cursor:pointer; font-size:clamp(26px,6vw,38px); }

    /* header */
    .bd-header{ position:relative; z-index:3; display:flex; align-items:center; justify-content:space-between; gap:12px; max-width:1180px; width:100%; margin:0 auto; padding:20px 20px 0; }
    .bd-right{ display:flex; align-items:center; gap:12px; }
    .bd-round{ position:relative; width:46px; height:46px; border-radius:50%; border:2px solid var(--ink); background:linear-gradient(180deg,#fff,#f0f0f0); display:flex; align-items:center; justify-content:center; font-size:20px; font-weight:700;
      box-shadow:inset 0 2px 1px #fff, 0 0 0 4px var(--yel), 0 0 0 6px var(--ink); transition:all .25s; }
    .bd-round:hover{ background:linear-gradient(180deg,#FF5C95,var(--rasp)); color:#fff; transform:translateY(-2px); }
    .bd-badge{ position:absolute; top:-12px; right:-12px; min-width:22px; height:22px; padding:0 6px; border-radius:999px; background:var(--red); color:#fff; border:2px solid #fff; font-style:normal; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; }

    /* buttons (same bubble style as Home) */
    .bt-btn{ position:relative; isolation:isolate; overflow:hidden; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:46px; padding:0 26px; border-radius:999px;
      background:linear-gradient(180deg,#FF5C95 0%,var(--rasp) 52%,#B80848 100%); border:2px solid var(--ink); color:#fff !important;
      font-size:13px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; white-space:nowrap;
      box-shadow:inset 0 2px 2px rgba(255,255,255,.55), inset 0 -4px 6px rgba(0,0,0,.2), 0 5px 0 var(--rasp-dark), 0 12px 18px -8px rgba(0,0,0,.45);
      transition:background .25s, color .25s, transform .15s, box-shadow .15s; }
    .bt-btn::before{ content:""; position:absolute; z-index:-1; left:8%; right:8%; top:3px; height:44%; border-radius:999px; background:linear-gradient(180deg,rgba(255,255,255,.55),rgba(255,255,255,.04)); }
    .bt-btn:hover:not(:disabled){ background:linear-gradient(180deg,#FFF27A 0%,var(--yel) 52%,#F2B800 100%); color:var(--ink) !important; transform:translateY(-2px);
      box-shadow:inset 0 2px 2px rgba(255,255,255,.8), inset 0 -4px 6px rgba(160,100,0,.2), 0 7px 0 var(--yel-dark), 0 16px 22px -8px rgba(242,184,0,.6); }
    .bt-btn:active:not(:disabled){ transform:translateY(4px); box-shadow:inset 0 2px 2px rgba(255,255,255,.6), 0 1px 0 var(--yel-dark); }
    .bt-btn.big{ height:56px; font-size:14px; }
    .bt-btn.block{ width:100%; margin-top:14px; }
    .bt-btn.grow{ flex:1; }
    .bt-btn.sun{ background:linear-gradient(180deg,#FFF27A 0%,var(--yel) 52%,#F2B800 100%); color:var(--ink) !important;
      box-shadow:inset 0 2px 2px rgba(255,255,255,.8), inset 0 -4px 6px rgba(160,100,0,.2), 0 5px 0 var(--yel-dark), 0 12px 18px -8px rgba(242,184,0,.6); }
    .bt-btn.sun:hover:not(:disabled){ background:linear-gradient(180deg,#FF5C95 0%,var(--rasp) 52%,#B80848 100%); color:#fff !important;
      box-shadow:inset 0 2px 2px rgba(255,255,255,.55), inset 0 -4px 6px rgba(0,0,0,.2), 0 7px 0 var(--rasp-dark), 0 16px 22px -8px rgba(227,11,92,.6); }

    .bd-main{ position:relative; z-index:2; width:100%; max-width:1100px; margin:0 auto; padding:28px 18px 10px; }
    .bd-back{ display:inline-flex; align-items:center; gap:8px; height:38px; padding:0 18px; margin-bottom:18px; border-radius:999px; border:2px solid var(--ink); background:#fff;
      font-size:12px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; box-shadow:0 4px 0 var(--ink); transition:all .2s; }
    .bd-back:hover{ background:var(--pink); transform:translateY(-2px); }

    /* the product "sticker card" */
    .bd-product{ display:grid; grid-template-columns:minmax(0,1fr); gap:26px; padding:20px; border-radius:36px; border:3px solid var(--ink); background:#fff;
      box-shadow:0 0 0 5px var(--yel), 0 0 0 8px var(--ink), 0 30px 40px -24px rgba(0,0,0,.55); }
    @media (min-width:860px){ .bd-product{ grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:38px; padding:30px; } }

    .bd-gal{ min-width:0; }
    .bd-frame{ position:relative; aspect-ratio:4/5; border-radius:28px; overflow:hidden; border:3px solid var(--ink); background:#FFE3F1;
      box-shadow:inset 0 3px 0 rgba(255,255,255,.7), 0 0 0 5px var(--pink), 0 0 0 7px var(--ink); display:flex; align-items:center; justify-content:center; }
    .bd-frame img{ width:100%; height:100%; object-fit:cover; display:block; user-select:none; animation:bdIn .35s ease-out; }
    @keyframes bdIn{ from{ opacity:0; transform:scale(1.03); } to{ opacity:1; transform:scale(1); } }
    .bd-noimg{ font-size:64px; color:var(--pink); }
    .bd-sticker{ position:absolute; z-index:3; top:14px; left:14px; padding:5px 14px; border-radius:999px; background:var(--rasp); color:#fff; border:2px solid #fff; font-size:12px; font-weight:700; letter-spacing:.12em; text-transform:uppercase; transform:rotate(-6deg); box-shadow:0 3px 0 var(--ink); }
    .bd-arrow{ position:absolute; z-index:3; top:50%; transform:translateY(-50%); width:42px; height:42px; border-radius:50%; border:2px solid var(--ink); background:#fff; font-size:26px; line-height:1; display:flex; align-items:center; justify-content:center; box-shadow:0 3px 0 var(--ink); transition:all .2s; }
    .bd-arrow:hover{ background:var(--yel); }
    .bd-arrow.l{ left:12px; } .bd-arrow.r{ right:12px; }
    .bd-thumbs{ display:flex; gap:12px; margin-top:20px; padding:6px 6px 8px; overflow-x:auto; scrollbar-width:none; }
    .bd-thumbs::-webkit-scrollbar{ display:none; }
    .bd-thumb{ flex:none; width:62px; height:78px; padding:0; border-radius:16px; overflow:hidden; border:2px solid var(--ink); background:#FFE3F1; transition:all .2s; }
    .bd-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
    .bd-thumb:hover{ transform:translateY(-3px); }
    .bd-thumb.on{ box-shadow:0 0 0 3px var(--yel), 0 0 0 5px var(--ink); }

    .bd-info{ display:flex; flex-direction:column; min-width:0; }
    .bd-name{ margin:4px 0 0; font-family:'Chewy','Fredoka',cursive !important; font-weight:400; font-size:clamp(34px,6vw,54px); line-height:1.02; letter-spacing:.02em; color:var(--rasp); text-shadow:0 3px 0 var(--pink-soft); }
    .bd-pricerow{ display:flex; align-items:center; flex-wrap:wrap; gap:12px; margin-top:14px; }
    .bd-price{ display:inline-flex; padding:6px 18px; border-radius:999px; background:var(--yel); border:2px solid var(--ink); font-size:24px; font-weight:700; box-shadow:0 4px 0 var(--ink); }
    .bd-stock{ display:inline-flex; padding:5px 14px; border-radius:999px; background:var(--lime); border:2px solid var(--ink); font-size:11px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; }
    .bd-stock.low{ background:var(--rasp); color:#fff; }
    .bd-desc{ margin:18px 0 0; font-size:16px; line-height:1.55; font-weight:400; color:#3a2a35; white-space:pre-line; max-width:56ch; }

    .bd-opt{ margin-top:20px; }
    .bd-opt h3{ margin:0 0 10px; font-size:14px; font-weight:600; letter-spacing:.06em; }
    .bd-opt h3 span{ color:var(--rasp); }
    .bd-colors{ display:flex; flex-wrap:wrap; gap:12px; }
    .bd-dot{ width:44px; height:44px; padding:4px; border-radius:50%; border:2px solid transparent; transition:all .2s; }
    .bd-dot i{ display:block; width:100%; height:100%; border-radius:50%; border:2px solid var(--ink); box-shadow:inset 2px 3px 4px rgba(255,255,255,.55), inset -3px -4px 6px rgba(0,0,0,.2); }
    .bd-dot:hover{ transform:translateY(-3px) scale(1.08); }
    .bd-dot.on{ border-color:var(--ink); box-shadow:0 0 0 3px var(--yel); }
    .bd-dot.gone{ opacity:.4; }
    .bd-sizes{ display:flex; flex-wrap:wrap; gap:10px; }
    .bd-size{ min-width:48px; height:42px; padding:0 16px; border-radius:999px; border:2px solid var(--ink); background:#fff; font-size:13px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; box-shadow:0 4px 0 var(--ink); transition:all .2s; }
    .bd-size:hover:not(:disabled){ background:var(--pink); transform:translateY(-2px); }
    .bd-size.on{ background:var(--ink); color:#fff; }
    .bd-size:disabled{ background:#f2e6ee; color:#a99aa4; text-decoration:line-through; box-shadow:none; border-color:#cdbbc6; }

    .bd-specs{ display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:20px; }
    .bd-specs div{ padding:10px 14px; border-radius:18px; border:2px dashed var(--pink); background:#FFF7FB; }
    .bd-specs small{ display:block; font-size:10px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:var(--rasp); margin-bottom:2px; }
    .bd-specs span{ font-size:14px; font-weight:500; }

    .bd-note{ margin:14px 0 0; font-size:13px; font-weight:600; color:var(--rasp); }
    .bd-total{ display:flex; align-items:baseline; justify-content:space-between; margin-top:22px; padding-top:16px; border-top:3px dotted var(--pink); }
    .bd-total span{ font-size:13px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; opacity:.7; }
    .bd-total b{ font-family:'Chewy','Fredoka',cursive; font-weight:400; font-size:34px; color:var(--rasp); }
    .bd-buyrow{ display:flex; align-items:center; gap:12px; margin-top:12px; }
    .bd-qty{ display:inline-flex; align-items:center; height:46px; border:2px solid var(--ink); border-radius:999px; overflow:hidden; background:#fff; box-shadow:0 4px 0 var(--ink); }
    .bd-qty button{ width:42px; height:100%; font-size:22px; font-weight:600; display:flex; align-items:center; justify-content:center; transition:background .2s; }
    .bd-qty button:hover:not(:disabled){ background:var(--yel); }
    .bd-qty b{ min-width:28px; text-align:center; font-size:16px; font-weight:600; }

    /* related */
    .bd-related{ position:relative; z-index:2; width:100%; max-width:1100px; margin:0 auto; padding:54px 18px 0; }
    .bd-rtitle{ margin:0; text-align:center; font-family:'Chewy','Fredoka',cursive !important; font-weight:400; font-size:clamp(28px,5.4vw,42px); letter-spacing:.05em; color:var(--pink);
      paint-order:stroke fill; -webkit-text-stroke:6px var(--ink); text-shadow:0 4px 0 var(--rasp); }
    .bd-rsub{ margin:14px 0 0; text-align:center; font-size:11px; font-weight:600; letter-spacing:.3em; text-transform:uppercase; opacity:.65; }
    .bd-rgrid{ display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:22px 18px; padding:30px 8px 6px; }
    @media (min-width:640px){ .bd-rgrid{ grid-template-columns:repeat(3,minmax(0,1fr)); } }
    @media (min-width:960px){ .bd-rgrid{ grid-template-columns:repeat(4,minmax(0,1fr)); } }
    .bd-rcard{ --c:var(--yel); position:relative; isolation:isolate; display:block; width:100%; aspect-ratio:3/4; padding:0; border-radius:24px; overflow:hidden; border:2px solid var(--ink); background:#FFE3F1; text-align:left;
      box-shadow:inset 0 3px 0 rgba(255,255,255,.6), 0 0 0 4px var(--c), 0 0 0 6px var(--ink), 0 14px 18px -10px rgba(0,0,0,.6);
      transition:transform .3s cubic-bezier(.2,.9,.3,1.3), box-shadow .3s; }
    .bd-rgrid > .bd-rcard:nth-child(4n+1){ --c:var(--yel); }
    .bd-rgrid > .bd-rcard:nth-child(4n+2){ --c:var(--pink); }
    .bd-rgrid > .bd-rcard:nth-child(4n+3){ --c:var(--lime); }
    .bd-rgrid > .bd-rcard:nth-child(4n+4){ --c:#ff7a3d; }
    .bd-rcard img{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; display:block; user-select:none; transition:transform .5s cubic-bezier(.22,.8,.26,1); }
    .bd-rcard::after{ content:""; position:absolute; z-index:1; left:0; right:0; bottom:0; height:55%; pointer-events:none; background:linear-gradient(180deg,rgba(0,0,0,0),rgba(0,0,0,.72)); }
    .bd-rcard:hover{ transform:translateY(-8px) rotate(-1deg) scale(1.03); box-shadow:inset 0 3px 0 rgba(255,255,255,.6), 0 0 0 5px var(--c), 0 0 0 8px var(--ink), 0 24px 30px -12px rgba(0,0,0,.7); }
    .bd-rcard:hover img{ transform:scale(1.07); }
    .bd-rchip{ position:absolute; z-index:3; top:10px; right:10px; padding:4px 10px; border-radius:999px; background:var(--c); color:var(--ink); border:1.5px solid var(--ink); box-shadow:0 2px 0 var(--ink); font-size:10px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; }
    .bd-rinfo{ position:absolute; z-index:3; left:12px; right:12px; bottom:12px; display:flex; flex-direction:column; align-items:flex-start; gap:6px; color:#fff; }
    .bd-rinfo b{ max-width:100%; font-family:'Chewy','Fredoka',cursive; font-weight:400; font-size:19px; letter-spacing:.03em; line-height:1.1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; text-shadow:0 2px 0 rgba(0,0,0,.6); }
    .bd-rinfo em{ font-style:normal; padding:3px 12px; border-radius:999px; background:#fff; color:var(--ink); border:1.5px solid var(--ink); box-shadow:0 3px 0 var(--ink); font-size:14px; font-weight:700; transition:background .2s, color .2s; }
    .bd-rcard:hover .bd-rinfo em{ background:var(--rasp); color:#fff; }

    /* states */
    .bd-state{ position:relative; z-index:2; display:flex; flex-direction:column; align-items:center; gap:14px; text-align:center; padding:80px 20px; }
    .bd-state h2{ margin:0; font-family:'Chewy','Fredoka',cursive !important; font-weight:400; font-size:34px; color:var(--rasp); letter-spacing:.04em; }
    .bd-state p{ margin:0; font-size:15px; opacity:.7; }
    .bd-heart{ font-size:54px; color:var(--pink); animation:bdBeat 1.2s ease-in-out infinite; }
    @keyframes bdBeat{ 0%,100%{ transform:scale(1); } 50%{ transform:scale(1.18); } }

    .bd-toast{ position:fixed; left:50%; bottom:calc(28px + env(safe-area-inset-bottom)); transform:translateX(-50%); z-index:500; max-width:calc(100% - 32px); padding:12px 24px; border-radius:999px;
      background:#fff; border:2px solid var(--ink); font-size:13px; font-weight:600; letter-spacing:.06em; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
      box-shadow:0 0 0 4px var(--yel), 0 0 0 6px var(--ink); animation:bdToast .25s ease-out; }
    @keyframes bdToast{ from{ opacity:0; transform:translate(-50%,10px); } to{ opacity:1; transform:translate(-50%,0); } }

    .bd-gap{ height:50px; }

    @media (max-width:640px){
      .bd-main{ padding:22px 10px 6px; }
      .bd-product{ padding:14px; border-radius:30px; }
      .bd-specs{ grid-template-columns:1fr; }
      .bd-flower.a{ width:150px; height:150px; left:-70px; }
      .bd-flower.b{ width:120px; height:120px; right:-55px; }
      .bd-flower.c{ width:170px; height:170px; right:-85px; }
      .bd-flower.d{ width:110px; height:110px; left:-50px; }
    }
    @media (min-width:768px){ .bd-header{ padding-left:48px; padding-right:48px; } }
    @media (prefers-reduced-motion: reduce){
      .bd-heart, .bd-toast, .bd-frame img{ animation:none; }
      .bd-root *{ transition:none !important; }
    }
  `;

  return (
    <div className="bd-root">
      <style>{css}</style>
      <div className="bd-stripes" aria-hidden="true" />
      <Flower className="bd-flower a" petal={RASP} centre={YEL} />
      <Flower className="bd-flower b" petal={LIME} centre={RASP} />
      <Flower className="bd-flower c" petal={YEL} centre={RASP} />
      <Flower className="bd-flower d" petal={PINK} centre={LIME} />

      <header className="bd-header">
        <h2 className="bd-mark" onClick={() => navigate("/")}>{BRAND}</h2>
        <div className="bd-right">
          <button type="button" className="bd-round" aria-label="Open bag" onClick={() => navigate(CHECKOUT_PATH, { state: { items: cartItems } })}>
            🛍
            {cartCount > 0 && <i className="bd-badge">{cartCount > 99 ? "99+" : cartCount}</i>}
          </button>
        </div>
      </header>

      <main className="bd-main">
        <button type="button" className="bd-back" onClick={() => navigate(-1)}>‹ Back</button>

        {loading ? (
          <div className="bd-state">
            <span className="bd-heart">♡</span>
            <h2>Fetching your cutie…</h2>
          </div>
        ) : !product ? (
          <div className="bd-state">
            <span className="bd-heart">✧</span>
            <h2>Oops, we can't find this one</h2>
            <p>It may have sold out or moved. Let's find you something else.</p>
            <button type="button" className="bt-btn big" onClick={() => navigate("/explore")}>Explore ✦</button>
          </div>
        ) : (
          <ProductView key={product.productId} p={product} all={allProducts ?? []} />
        )}
      </main>

      <div className="bd-gap" />
      <Footer />
    </div>
  );
};

export default Details;
